import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseSidePanelSelectionResult<T> {
  selected: T | null;
  isOpen: boolean;
  select: (item: T) => void;
  close: () => void;
  requestClose: () => void;
  cancelPendingClose: () => void;
}

export const useSidePanelSelection = <T>(): UseSidePanelSelectionResult<T> => {
  const [selected, setSelected] = useState<T | null>(null);
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingClose = useCallback(() => {
    if (pendingRef.current != null) {
      clearTimeout(pendingRef.current);
      pendingRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelPendingClose(), [cancelPendingClose]);

  const select = useCallback(
    (item: T) => {
      cancelPendingClose();
      setSelected(item);
    },
    [cancelPendingClose]
  );

  const close = useCallback(() => {
    cancelPendingClose();
    setSelected(null);
  }, [cancelPendingClose]);

  const requestClose = useCallback(() => {
    if (pendingRef.current != null) return;
    pendingRef.current = setTimeout(() => {
      pendingRef.current = null;
      setSelected(null);
    }, 0);
  }, []);

  return {
    selected,
    isOpen: selected != null,
    select,
    close,
    requestClose,
    cancelPendingClose,
  };
};
